/**
 * Client-side image compression utility
 * Compresses images before uploading to reduce file size
 */

import heic2any from 'heic2any';

export interface CompressionOptions {
  maxSizeMB: number;
  maxWidthOrHeight?: number;
  quality?: number;
}

/**
 * Convert HEIC/HEIF to JPEG if needed
 */
const convertHeicIfNeeded = async (file: File): Promise<File> => {
  // Check if file is HEIC/HEIF
  const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
      file.name.toLowerCase().endsWith('.heic') || 
      file.name.toLowerCase().endsWith('.heif');
  if (isHeic) {
    try {
      // Convert HEIC to JPEG using heic2any
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9
      });
      
      // heic2any returns an array, take the first result
      const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
      
      // Validate that we got a valid blob
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('HEIC conversion returned invalid blob');
      }
      
      // Create a new File object from the converted blob
      const convertedFile = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
        type: 'image/jpeg',
        lastModified: file.lastModified
      });
      
      // Verify the converted file is actually JPEG (defensive check)
      if (convertedFile.type !== 'image/jpeg' && !convertedFile.name.toLowerCase().endsWith('.jpg') && !convertedFile.name.toLowerCase().endsWith('.jpeg')) {
        throw new Error(`HEIC conversion failed: resulting file type is ${convertedFile.type}, expected image/jpeg`);
      }
      
      return convertedFile;
    } catch (error) {
      console.error('HEIC conversion failed:', error);
      throw new Error('Failed to convert HEIC image. Please try converting it to JPEG first.');
    }
  }
  
  return file;
};

/**
 * Compress an image file to meet size requirements
 * @param file - The original image file
 * @param options - Compression options
 * @returns Promise<string> - Base64 encoded compressed image
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions
): Promise<string> => {
  const { maxSizeMB, maxWidthOrHeight = 1920, quality = 0.8 } = options;

  return new Promise(async (resolve, reject) => {
    try {
      // Convert HEIC to JPEG if needed
      const processedFile = await convertHeicIfNeeded(file);
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        
        // Defensive check: Verify the processed file data URL is not HEIC
        // This catches cases where conversion failed but didn't throw an error
        if (dataUrl && (dataUrl.includes('image/heic') || dataUrl.includes('image/heif'))) {
          reject(new Error('HEIC file detected after processing. Conversion may have failed. Please try converting the image to JPEG first.'));
          return;
        }
        
        const img = new Image();
        
        img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (maxWidthOrHeight && (width > maxWidthOrHeight || height > maxWidthOrHeight)) {
          if (width > height) {
            height = (height / width) * maxWidthOrHeight;
            width = maxWidthOrHeight;
          } else {
            width = (width / height) * maxWidthOrHeight;
            height = maxWidthOrHeight;
          }
        }
        
        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress iteratively until size is acceptable
        let currentQuality = quality;
        const targetSizeBytes = maxSizeMB * 1024 * 1024;
        
        const compressIteratively = () => {
          const compressedDataUrl = canvas.toDataURL('image/jpeg', currentQuality);
          
          // CRITICAL: Verify the data URL has the correct MIME type before sending to backend
          // This prevents HEIC files from reaching the backend even if conversion failed silently
          if (!compressedDataUrl.startsWith('data:image/jpeg') && !compressedDataUrl.startsWith('data:image/jpg')) {
            const dataUrlHeader = compressedDataUrl.split(',')[0];
            reject(new Error(`Invalid image format after compression: ${dataUrlHeader}. Expected data:image/jpeg. This may indicate HEIC conversion failed.`));
            return;
          }
          
          // Estimate size of base64 string (approximately)
          const base64Length = compressedDataUrl.split(',')[1].length;
          const estimatedSizeBytes = (base64Length * 3) / 4;
          
          // If size is acceptable or quality is too low, return result
          if (estimatedSizeBytes <= targetSizeBytes || currentQuality <= 0.1) {
            resolve(compressedDataUrl);
          } else {
            // Reduce quality and try again
            currentQuality -= 0.1;
            compressIteratively();
          }
        };
        
        compressIteratively();
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target?.result as string;
    };
    
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(processedFile);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Check if a base64 image string is within size limit
 * @param base64String - The base64 encoded image
 * @param maxSizeMB - Maximum size in MB
 * @returns boolean - True if within limit
 */
export const isImageWithinSizeLimit = (base64String: string, maxSizeMB: number): boolean => {
  try {
    // Remove data URL prefix if present
    const base64Data = base64String.includes(',') 
      ? base64String.split(',')[1] 
      : base64String;
    
    // Calculate size in bytes
    const sizeInBytes = (base64Data.length * 3) / 4;
    const sizeInMB = sizeInBytes / (1024 * 1024);
    
    return sizeInMB <= maxSizeMB;
  } catch {
    return false;
  }
};

/**
 * Get the size of a base64 image in MB
 * @param base64String - The base64 encoded image
 * @returns number - Size in MB
 */
export const getBase64ImageSize = (base64String: string): number => {
  try {
    const base64Data = base64String.includes(',') 
      ? base64String.split(',')[1] 
      : base64String;
    
    const sizeInBytes = (base64Data.length * 3) / 4;
    return sizeInBytes / (1024 * 1024);
  } catch {
    return 0;
  }
};

